/**
 * CAPTURA UNIVERSAL (F13.3) — contrato con el resolvedor del servidor
 *
 * Pistola, cámara y teclado mandan el MISMO texto crudo a
 * `POST /api/inventario/escaneo`; toda la interpretación (etiqueta NeatOS,
 * GS1, códigos, ERP, lote, serie) vive en el servidor. Aquí sólo se capturan
 * y se pintan los resultados.
 */
export type UsoEscaneo = 'ENTRADA' | 'SALIDA' | 'TRASLADO' | 'AJUSTE' | 'INVENTARIO' | 'CONSUMO_PT' | 'CONSUMO_OT' | 'DEVOLUCION' | 'CONSULTA';

export interface ContextoEscaneo {
  idAlmacen?: string | null;
  idOperacion?: string | null;
  idArticulo?: string | null;
  uso: UsoEscaneo;
}

export interface ArticuloEscaneo {
  id: string; codigo: string; codigoNegocio: string; descripcion: string; activo: boolean; inventariable: boolean;
  controlaLotes: boolean; controlaSeries: boolean; controlaCaducidad: boolean;
  unidadStock: { id: string; codigo: string; decimales: number };
}
export interface LoteEscaneo { id: string; codigo: string; fechaCaducidad: string | null; estado: 'ACTIVO' | 'BLOQUEADO'; activo: boolean }
export interface SerieEscaneo { id: string; numero: string; idLote: string | null }
export interface RecursoEscaneo {
  tipo: 'ARTICULO' | 'LOTE' | 'SERIE' | 'ALMACEN';
  articulo: ArticuloEscaneo | null;
  lote: LoteEscaneo | null;
  serie: SerieEscaneo | null;
  almacen: { id: string; codigo: string; codigoNegocio?: string; descripcion: string } | null;
  enAlmacen: { saldo: string | null; serieEnStock: boolean | null } | null;
}
export interface ResultadoEscaneo {
  resultado: 'RESUELTO' | 'AMBIGUO' | 'DESCONOCIDO';
  origen: string | null;
  motivo: string | null;
  valor: string;
  recurso: RecursoEscaneo | null;
  candidatos: RecursoEscaneo[];
  cantidad: number | null;
  lotePendiente: { codigo: string; fechaCaducidad: string | null; fechaFabricacion: string | null } | null;
  seriePendiente: string | null;
  puedeCrearLote: boolean;
  avisos: string[];
  pendiente: { id: string; repeticiones: number } | null;
  /** F13.3.1: cómo se leyó («QR», «EAN-13»…), sólo informativo. El usuario nunca lo indica. */
  formato?: string | null;
}

/** Lo que el escáner entrega a la pantalla: un recurso identificado (elegido por el usuario si era ambiguo). */
export interface Lectura {
  recurso: RecursoEscaneo;
  /** Cantidad leída (GS1 30/37) o null: la pantalla suma 1 por lectura en fungibles. */
  cantidad: number | null;
  /** Número de serie leído que aún no existe (p. ej. GS1 21 en una entrada). */
  seriePendiente: string | null;
  valor: string;
}

/**
 * Respuesta de la pantalla a una lectura: null = aplicada (éxito); un texto = no se ha podido
 * aplicar (p. ej. «serie repetida»), y el escáner lo muestra como error.
 */
export type AlLeer = (l: Lectura) => Promise<string | null> | string | null;

export const textoRecurso = (r: RecursoEscaneo): string => {
  if (r.tipo === 'ALMACEN') return `Almacén ${r.almacen === null ? '' : (r.almacen.codigoNegocio ?? r.almacen.codigo)}`;
  const a = r.articulo;
  //  F13.5-A: el código de negocio; el token NeatOS no se muestra.
  const base = a === null ? '' : `${a.codigoNegocio} · ${a.descripcion}`;
  if (r.serie !== null) return `${base} · serie ${r.serie.numero}`;
  if (r.lote !== null) return `${base} · lote ${r.lote.codigo}`;
  return base;
};
